/**
 * Hooks de datos de adjuntos (TanStack Query). Clave de la caché: ['adjuntos', <tareaId>].
 * Al subir o eliminar también se refresca el detalle de la tarea, porque el historial trae el evento.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { eliminarAdjunto, leerTextoAdjunto, listarAdjuntos, subirAdjunto } from './api';

export function useAdjuntos(tareaId: number) {
  return useQuery({
    queryKey: ['adjuntos', tareaId],
    queryFn: () => listarAdjuntos(tareaId),
    refetchInterval: 30_000,
  });
}

/**
 * Texto de un adjunto `.txt` o `.csv` para el visor. Un adjunto no cambia de contenido (se borra y se
 * sube otro), así que no se vuelve a pedir mientras esté en caché.
 */
export function useTextoAdjunto(id: number, habilitado: boolean) {
  return useQuery({
    queryKey: ['adjunto-texto', id],
    queryFn: () => leerTextoAdjunto(id),
    enabled: habilitado,
    staleTime: Infinity,
  });
}

function useRefrescar(tareaId: number) {
  const cliente = useQueryClient();
  return () => {
    void cliente.invalidateQueries({ queryKey: ['adjuntos', tareaId] });
    void cliente.invalidateQueries({ queryKey: ['tarea', tareaId] });
  };
}

export function useSubirAdjunto(tareaId: number) {
  const refrescar = useRefrescar(tareaId);
  return useMutation({
    mutationFn: ({
      archivo,
      tipo,
      alProgresar,
    }: {
      archivo: File;
      tipo: string;
      alProgresar?: (fraccion: number) => void;
    }) => subirAdjunto(tareaId, archivo, tipo, alProgresar),
    onSettled: refrescar,
  });
}

export function useEliminarAdjunto(tareaId: number) {
  const refrescar = useRefrescar(tareaId);
  return useMutation({
    mutationFn: (id: number) => eliminarAdjunto(id),
    onSettled: refrescar,
  });
}
